import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIngredientPricesQuery, useGetIngredientsCodesQuery } from '../../features/ordersApiSlice';
import { useLocation } from 'react-router';
import Logo from '../../assets/images/pdf/gettested-logo.png';
import {
  CONSTANTS,
  EXTRA_UNITS,
  kcal_per_g_carbs,
  kj_per_g_carbs,
  MICRO_INGREDIENT_IDS,
} from '../../Data/OrdersPdf';
import { EXTERNAL_ORDER_TRANSLATIONS } from '../../translations/ordersPdf';
import Img1 from '../../assets/images/pdf/how-to-take-1.jpg';
import Img2 from '../../assets/images/pdf/how-to-take-2.jpg';
import Img3 from '../../assets/images/pdf/how-to-take-3.jpg';
import { downloadPdf, getBestBeforeDate } from '../../utils';
import { producedDays, monthsFromOrderMonths } from '../../utils/labelDays';

const ROW_WIDTH = 't-w-[200px]';

const GettestedOrderPdfDetails = () => {
  const location = useLocation();
  const { order } = location.state || {};
  console.log('orderData => ', order);
  const [lang, setLang] = useState('en');
  const orderDetailsRef = useRef();

  useEffect(() => {
    if (!order) return;
    setLang(['de', 'germany'].includes(order?.user?.country?.toLowerCase()) ? 'de' : 'en');
  }, [order]);

  const { data: ingredientPrices, isLoading: ingredientPricesLoading } = useGetIngredientPricesQuery(lang);
  const { data: ingredientCodes, isLoading: ingredientCodesLoading } = useGetIngredientsCodesQuery();

  const TRANSlATIONS = useMemo(() => EXTERNAL_ORDER_TRANSLATIONS[lang], [lang]);

  const HOW_TO_TAKE = [
    {
      heading: TRANSlATIONS.howToTakeStep1Heading,
      desc: TRANSlATIONS.howToTakeStep1Desc,
      img: Img1,
    },
    {
      heading: TRANSlATIONS.howToTakeStep2Heading,
      desc: TRANSlATIONS.howToTakeStep2Desc,
      img: Img2,
    },
    {
      heading: TRANSlATIONS.howToTakeStep3Heading,
      desc: TRANSlATIONS.howToTakeStep3Desc,
      img: Img3,
    },
  ];

  const ROW_CLASS = 't-px-2 t-text-xs';

  const page1Ref = useRef();
  const page2Ref = useRef();
  const page3Ref = useRef();

  const totalWeight = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const prices = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId)
      );
      const active = parseFloat(prices?.active_percentage);

      const hydroSum = (parseFloat(element?.quantity) * parseFloat(prices?.hydro_percentage)) / active;

      const saccharoseSum =
        (parseFloat(element?.quantity) * parseFloat(prices?.saccharose_percentage)) / active;

      const starchSum = (parseFloat(element?.quantity) * parseFloat(prices?.starch_percentage)) / active;

      const restSum =
        (parseFloat(element?.quantity) * parseFloat(prices?.rest_ingredients_percentage)) / active;

      const allSum = parseFloat(element?.quantity) + hydroSum + saccharoseSum + starchSum + restSum;

      sum = isNaN(allSum) ? 0 : sum + allSum;
    });
    const total = sum * producedDays(monthsFromOrderMonths(order?.order_months), order?.dosing_days);
    return Math.round(total / 1000);
  }, [order, ingredientPrices]);

  const bestBeforeDate = useMemo(() => getBestBeforeDate(order), [order]);

  const totalSaccharose = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId)
      );
      if (price) {
        const sumOfPrices =
          (parseFloat(price?.saccharose_percentage) * parseFloat(element?.quantity)) /
          parseFloat(price?.active_percentage);
        sum += sumOfPrices;
      }
    });
    return sum;
  }, [order]);

  const totalStarch = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find(
        (item) => item?.ingredient_id === parseInt(element?.ingredientId)
      );
      if (price) {
        sum +=
          (parseFloat(price?.starch_percentage) * parseFloat(element?.quantity)) /
          parseFloat(price?.active_percentage);
      }
    });
    return sum;
  }, [order]);

  const carbohydrate = useMemo(
    () => Math.round((totalSaccharose + totalStarch) / 1000),
    [totalSaccharose, totalStarch]
  );

  const carbohydratePercentage = useMemo(() => {
    const percentage = carbohydrate / CONSTANTS['Carbohydrates'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [carbohydrate]);

  const sugar = useMemo(() => Math.round(totalSaccharose / 1000), [totalSaccharose]);

  const sugarPercentage = useMemo(() => {
    const percentage = sugar / CONSTANTS['Sugars'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [sugar]);

  const energy = useMemo(
    () => kcal_per_g_carbs * carbohydrate + '/' + kj_per_g_carbs * carbohydrate,
    [carbohydrate]
  );

  const energyPercentage = useMemo(() => {
    const energyPerc = (kcal_per_g_carbs * carbohydrate) / CONSTANTS['Enerygy'];
    return energyPerc < 1 ? '<1%' : energyPerc.toFixed(2) + '%';
  }, [carbohydrate]);

  const ingredients = useMemo(
    () =>
      ingredientPrices?.data?.filter((item) =>
        order?.ingredients?.some((orderIng) => parseInt(orderIng.ingredientId) === item.ingredient_id)
      ) || [],
    [ingredientPrices?.data, order?.ingredients]
  );

  const ingredientLabelString = useMemo(
    () =>
      ingredients
        ?.map((item) => `${item?.ingredient?.ingredient} (${item?.ingredient?.scientific_name})`)
        .filter(Boolean)
        .join(', '),
    [ingredients]
  );

  if (ingredientCodesLoading || ingredientPricesLoading) {
    return <div>Loading...</div>;
  }
  return (
    <>
      <button onClick={() => downloadPdf([page1Ref, page2Ref], order?.internal_order_id ?? order?.order_id)}>
        Download
      </button>
      <div
        className="t-w-[794px] t-mx-auto t-flex t-flex-col"
        ref={orderDetailsRef}
        style={{
          fontFamily: 'Barlow',
        }}
      >
        {/* First Page */}
        <div className="t-relative t-flex t-flex-col t-gap-4 t-px-12 t-pt-8 t-pb-4" ref={page1Ref}>
          <div className="t-w-full t-flex t-flex-col t-items-center t-justify-center t-gap-2">
            <img src={Logo} alt="logo" className="t-w-[40%] t-text-center" />
            <h1 className="t-text-3xl t-font-bolder">
              {TRANSlATIONS.hi}{' '}
              <span className="t-text-[#006f97] t-font-semibold t-capitalize">{order?.user?.full_name}</span>
            </h1>
            <h1 className="t-text-xl t-text-[#006f97] t-font-semibold t-mt-4">{TRANSlATIONS.welcome}!</h1>
            <p className="t-text-sm">{TRANSlATIONS.weAreExcited}</p>
          </div>
          <div className="t-flex t-flex-col t-items-center t-justify-center">
            <div className="">
              <h1 className="t-text-xl t-text-[#006f97] t-font-semibold">{TRANSlATIONS.yourDailyFormula}</h1>
            </div>
            <table className="t-mt-8">
              <thead>
                <tr className="t-font-bold">
                  <td className="t-pr-2 t-text-sm">{TRANSlATIONS.nutrient}</td>
                  <td className={`${ROW_CLASS} !t-text-end t-min-w-[80px]`}>{TRANSlATIONS.dailyDose}</td>
                  <td className={`${ROW_CLASS} t-min-w-[60px]`}> {TRANSlATIONS.unit}</td>
                  <td className={`${ROW_CLASS} !t-text-end t-min-w-[60px]`}>% NRV</td>
                </tr>
              </thead>
              <tbody>
                {order?.ingredients?.map((item) => {
                  const ingredientData = ingredientCodes?.data?.find(
                    (code) => code?.code === parseInt(item?.ingredientId)
                  );
                  const quantity = parseFloat(item?.quantity);
                  const euNrv = parseFloat(ingredientData?.eu_nrv);
                  const nrv =
                    parseFloat(ingredientData?.eu_nrv) <= 0 ? 0 : parseInt((quantity / euNrv) * 100);
                  return (
                    <tr key={item.id}>
                      <td className="t-pr-2 t-text-sm">{item?.ingredientName}</td>
                      <td className={`${ROW_CLASS} !t-text-end `}>
                        {MICRO_INGREDIENT_IDS?.includes(item?.ingredientId)
                          ? (parseFloat(item?.quantity) * 1000).toFixed(2)
                          : item?.quantity}
                      </td>
                      <td className={`${ROW_CLASS}`}>
                        {MICRO_INGREDIENT_IDS?.includes(item?.ingredientId) ? 'µg' : 'mg'}
                        {Object.keys(EXTRA_UNITS)?.includes(item?.ingredientId?.toString())
                          ? ' ' + EXTRA_UNITS[item?.ingredientId]
                          : ''}
                      </td>
                      <td className={`${ROW_CLASS} !t-text-end`}>
                        {nrv === 0 || isNaN(nrv) ? '*' : nrv + '%'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="t-mt-8 t-text-sm">
            {TRANSlATIONS.our}{' '}
            <span className="t-text-[#006f97] t-font-semibold">{TRANSlATIONS.expertCommittee}</span>{' '}
            {TRANSlATIONS.ourExpertCommittee}
          </p>
          <p className="t-mt-8 t-text-sm t-text-left">{TRANSlATIONS.artificialFlavours}</p>
        </div>
        {/* Second Page */}
        <div className="t-relative t-flex t-flex-col t-gap-8 t-p-12" ref={page2Ref}>
          <h1 className="t-text-4xl t-font-bolder t-text-center t-w-full t-mb-8">
            {TRANSlATIONS.howToTakeHeading}
          </h1>
          <div className="t-flex t-flex-col">
            <p className="t-text-lg t-text-[#006f97] t-font-bold">{TRANSlATIONS.startNewHabbit}</p>
            <p className="t-text-lg">{TRANSlATIONS.startNewHabbitDesc}</p>
          </div>
          <div className="t-flex t-flex-col">
            <p className="t-text-lg t-text-[#006f97] t-font-bold">{TRANSlATIONS.howToTakeHeading}</p>
            <p className="t-text-lg">{TRANSlATIONS.howToTakeDesc}</p>
            <p className="t-text-lg">{TRANSlATIONS.howToTakeDesc2}</p>
          </div>
          <div className="t-flex t-items-stretch t-justify-center t-gap-16">
            {HOW_TO_TAKE.map((item, i) => (
              <div className="t-relative t-flex t-flex-col t-items-center t-justify-center t-gap-2">
                <p className="t-h-7 t-w-7 t-bg-[#006f97] t-rounded-full t-text-white t-text-xs t-pl-[11px]">
                  {i + 1}
                </p>
                <p className="t-text-[#006f97] t-text-center t-max-w-[220px] t-mt-auto t-text-base">
                  {item.desc}
                </p>
                <img src={item.img} alt="" className="t-h-40 t-w-40 t-mt-auto" />
              </div>
            ))}
          </div>
          <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-4 t-mt-48">
            <p className="t-text-center t-text-lg">
              {TRANSlATIONS.anyQueries}{' '}
              <span className="">
                <a
                  href="mailto:hello@gettested.io"
                  className="t-text-[#006f97] t-font-semibold hover:t-text-[#006f97]"
                >
                  hello@gettested.io
                </a>
              </span>
            </p>
            <img src={Logo} alt="logo" className="t-w-[40%] t-text-center t-mb-8" />
          </div>
        </div>

        {/* Third Page */}
        <div className="t-relative t-flex t-flex-col t-gap-4 t-p-12" ref={page3Ref}>
          <table>
            <tbody className="t-text-[10px]">
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.productName}</td>
                <td>Get Tested Custom supplements</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.netQuantity}</td>
                <td>{totalWeight ?? 0}g</td>
              </tr>

              <tr className="">
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.ingredients}</td>
                <td className="t-leading-3">
                  {ingredientLabelString}, {TRANSlATIONS.ingredientsDesc}
                </td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.instructionsForUse}</td>
                <td>{TRANSlATIONS.instructionsForUseDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.allergyInformation}</td>
                <td className="t-leading-3">{TRANSlATIONS.allergyInformationDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.warnings}</td>
                <td className="t-leading-3">{TRANSlATIONS.warningsDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.storageInstructions}</td>
                <td>{TRANSlATIONS.storageInstructionsDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.bestBeforeDate}</td>
                <td>{bestBeforeDate}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.manufacturerInformation}</td>
                <td>Gettested International AB Mörtnäs Hagväg 3, Lokal 11 134 44 Gustavsberg Sweden</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.countryOfManufacture}</td>
                <td>{TRANSlATIONS.countryOfManufactureDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.customerService}</td>
                <td>{TRANSlATIONS.customerServiceDesc}</td>
              </tr>
              <tr>
                <td className={`${ROW_WIDTH}`}>{TRANSlATIONS.batchNumber}</td>
                <td>{order?.internal_order_id}</td>
              </tr>
            </tbody>
          </table>
          <table className="t-max-w-[500px] t-text-[10px]">
            <thead>
              <tr>
                <td className={`${ROW_WIDTH} t-font-semibold`}>{TRANSlATIONS.ingredient} </td>
                <td className="t-font-semibold">{TRANSlATIONS.amountPerB}</td>
                <td className="t-font-semibold">% NRV</td>
              </tr>
            </thead>
            <tbody>
              {order?.ingredients?.map((item) => {
                const ingredientData = ingredientCodes?.data?.find(
                  (code) => code?.code === parseInt(item?.ingredientId)
                );
                const quantity = parseFloat(item?.quantity);
                const euNrv = parseFloat(ingredientData?.eu_nrv);
                const nrv = parseFloat(ingredientData?.eu_nrv) <= 0 ? 0 : parseInt((quantity / euNrv) * 100);
                return (
                  <tr key={item.id}>
                    <td className="">{item?.ingredientName}</td>
                    <td className={``}>
                      {MICRO_INGREDIENT_IDS?.includes(item?.ingredientId)
                        ? (parseFloat(item?.quantity) * 1000).toFixed(2)
                        : item?.quantity}
                      {MICRO_INGREDIENT_IDS?.includes(item?.ingredientId) ? ' µg' : ' mg'}
                      {Object.keys(EXTRA_UNITS)?.includes(item?.ingredientId?.toString())
                        ? ' ' + EXTRA_UNITS[item?.ingredientId]
                        : ''}
                    </td>
                    <td className={``}>{nrv === 0 || isNaN(nrv) ? '*' : nrv + '%'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <table className="t-max-w-[600px] t-text-[10px]">
            <thead>
              <tr>
                <td className={`${ROW_WIDTH} t-font-semibold`}>{TRANSlATIONS.nutrient} </td>
                <td className="t-font-semibold">{TRANSlATIONS.amountPerBag}</td>
                <td className="t-font-semibold">{TRANSlATIONS.referenceQuantity}</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{TRANSlATIONS.energy}</td>
                <td>{energy} kcal/kj</td>
                <td>{energyPercentage}</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.fat}</td>
                <td>0 g</td>
                <td>0%</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.saturatedFattyAcids}</td>
                <td>0 g</td>
                <td>0%</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.carbohydrates}</td>
                <td>{carbohydrate} g</td>
                <td>{carbohydratePercentage}</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.sugar}</td>
                <td>{sugar} g</td>
                <td>{sugarPercentage}</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.protein}</td>
                <td>0 g</td>
                <td>0%</td>
              </tr>
              <tr>
                <td>{TRANSlATIONS.salt}</td>
                <td>0 g</td>
                <td>0%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default GettestedOrderPdfDetails;
